/**
 * Generic API Factory Generator
 * Generates factory functions for API instances to reduce code duplication
 * All factories follow the same pattern: base factory -> instance factory -> singleton
 */

/**
 * Configuration for creating an API factory
 */
export interface ApiFactoryConfig<BaseInstance, ApiInstance> {
	/**
	 * Name of the API (for logging)
	 */
	name: string;
	/**
	 * Function to create the base instance (BaseNotary, BaseCustomer, etc.)
	 */
	createBase: () => BaseInstance;
	/**
	 * Function to create the API instance from the base instance
	 */
	createInstance: (base: BaseInstance) => ApiInstance;
}

/**
 * Create a factory function for an API instance
 * Follows the singleton pattern to avoid Webpack TDZ issues
 */
export function createApiFactory<BaseInstance, ApiInstance>(
	config: ApiFactoryConfig<BaseInstance, ApiInstance>,
): {
	getInstance: () => ApiInstance;
	create: () => ApiInstance;
} {
	const { createBase, createInstance } = config;

	// Module-level singleton instances
	let instance: ApiInstance | null = null;
	let baseInstance: BaseInstance | null = null;

	function create(): ApiInstance {
		if (!baseInstance) {
			baseInstance = createBase();
		}
		return createInstance(baseInstance);
	}

	function getInstance(): ApiInstance {
		if (!instance) {
			instance = create();
		}
		return instance;
	}

	return {
		getInstance,
		create,
	};
}
